import React, { useEffect, useMemo, useState } from "react";
import { Badge, Box, Button, Card, Dialog, Flex, SegmentedControl, Select, Switch, Table, Text, TextField,
  Tooltip, Separator, Callout } from "@radix-ui/themes";
import { ChatBubbleIcon, ChevronDownIcon, ChevronUpIcon, CheckCircledIcon, CopyIcon, EnvelopeClosedIcon,
  ExclamationTriangleIcon, MobileIcon, PlusIcon, ReloadIcon, TrashIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { useLocationContext, useProfileContext } from "context";
import { useCreateMarketingSenderDomain, useDeleteMarketingSenderDomain, useGetAllBrands, useMarketingSenderDomains,
  useEmailMarketingUsage, useUpdateEmailMarketingUsageLimit, useUpdateMarketingSenderDomain,
  useVerifyMarketingSenderDomain } from "hooks";
import { ToastAlert } from "components";
import { LocationMultiSelect } from "components/locationMultiSelect/LocationMultiSelect";
import { useToast } from "../../context/ToastContext";

const STATUS_COLOR = {
  pending: "amber",
  verified: "green",
  failed: "red",
};

// ── Mock data ──────────────────────────────────────────────────────────────────
const MOCK_CHANNELS = [
  {
    key: "email",
    label: "Email",
    icon: EnvelopeClosedIcon,
    iconColor: "var(--blue-9)",
    sent: 0,
    billable: 0,
    pending: 0,
    awaitingDelivery: 0,
    delivered: 0,
    bounced: 0,
    dropped: 0,
    remaining: null,
    cost: 0,
    limitAmount: 50000,
    limitStatus: false,
    costPerUnit: 0,
    quickLimits: [10000, 25000, 50000, 75000, 100000, 150000, 200000, 300000],
  },
  // {
  //   key: "sms",
  //   label: "SMS",
  //   icon: MobileIcon,
  //   iconColor: "var(--orange-9)",
  //   sent: 8942,
  //   cost: 447.1,
  //   limitAmount: 10000,
  //   limitStatus: true,
  //   costPerUnit: 447.1 / 8942,
  //   quickLimits: [5000, 10000, 25000, 50000, 75000, 100000, 150000, 200000],
  // },
  // {
  //   key: "whatsapp",
  //   label: "WhatsApp",
  //   icon: ChatBubbleIcon,
  //   iconColor: "var(--green-9)",
  //   sent: 3210,
  //   cost: 256.8,
  //   limitAmount: 5000,
  //   limitStatus: true,
  //   costPerUnit: 256.8 / 3210,
  //   quickLimits: [2500, 5000, 10000, 25000, 50000, 75000, 100000, 150000],
  // },
];

const CURRENCY = "£";
const CURRENCY_SYMBOLS = { gbp: "£", usd: "$", eur: "€" };

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n, decimals = 2) =>
  Number(n).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const fmtInt = (n) => Number(n).toLocaleString("en-GB");

const fmtUnitPrice = (n) =>
  Number(n).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });

const fmtDate = (value) =>
  new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const fmtDateTime = (value) =>
  new Date(value)
    .toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
    .replace(/\b(am|pm)\b/i, (period) => period.toUpperCase());

function estimateChargeForQuantity(config, quantity) {
  if (!config || !quantity || quantity <= 0) return 0;

  if (config.billing_scheme !== "tiered") {
    return Number(config.unit_amount || 0) * quantity;
  }

  const tiers = config.tiers || [];
  if (!tiers.length) return null;

  if (config.tiers_mode === "volume") {
    const selectedTier = tiers.find((tier) => tier.up_to == null || quantity <= tier.up_to) || tiers[tiers.length - 1];
    return Number(selectedTier.unit_amount || 0) * quantity + Number(selectedTier.flat_amount || 0);
  }

  let amount = 0;
  let previousUpTo = 0;
  for (const tier of tiers) {
    const tierLimit = tier.up_to == null ? quantity : Math.min(quantity, tier.up_to);
    const tierUnits = Math.max(tierLimit - previousUpTo, 0);
    if (tierUnits > 0) {
      amount += Number(tier.unit_amount || 0) * tierUnits + Number(tier.flat_amount || 0);
    }
    if (tier.up_to == null || quantity <= tier.up_to) break;
    previousUpTo = tier.up_to;
  }

  return amount;
}

function estimateChargeForQuantityConfigs(configs, quantity) {
  if (!configs?.length) return null;

  let canEstimateAllConfigs = true;
  const amount = configs.reduce((sum, config) => {
    const configAmount = estimateChargeForQuantity(config, quantity);
    if (configAmount == null) {
      canEstimateAllConfigs = false;
    }
    return configAmount == null ? sum : sum + configAmount;
  }, 0);

  return canEstimateAllConfigs ? amount : null;
}

function getPriceRateRows(config) {
  if (config?.billing_scheme !== "tiered") {
    return [{
      key: "all",
      range: "All emails",
      unitAmount: Number(config?.unit_amount || 0),
      flatAmount: 0,
    }];
  }

  let previousUpTo = 0;
  return (config.tiers || []).map((tier, index) => {
    const upTo = tier.up_to == null ? null : Number(tier.up_to);
    const from = previousUpTo + 1;
    const range = upTo == null ? `${fmtInt(from)}+` : `${fmtInt(from)} – ${fmtInt(upTo)}`;
    previousUpTo = upTo ?? previousUpTo;
    return {
      key: `${from}-${upTo ?? "inf"}-${index}`,
      range,
      unitAmount: Number(tier.unit_amount || 0),
      flatAmount: Number(tier.flat_amount || 0),
    };
  });
}

const getBarColor = (pct) => {
  if (pct >= 85) return "var(--orange-9)";
  if (pct >= 70) return "var(--amber-9)";
  return "var(--green-9)";
};

// ── Usage sub-components ───────────────────────────────────────────────────────
function ProgressBar({ value, max, color, height = 8 }) {
  const pct = Math.min((value / max) * 100, 100);
  const barColor = color || getBarColor(pct);
  return (
    <div
      style={{
        width: "100%",
        height,
        borderRadius: height / 2,
        background: "var(--gray-a4)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          borderRadius: height / 2,
          background: barColor,
          transition: "width 0.3s ease",
        }}
      />
    </div>
  );
}

function StripeRateCard({ configs, fallbackCurrency = CURRENCY }) {
  const [expanded, setExpanded] = useState(false);
  const availableConfigs = (configs || []).filter(Boolean);

  if (!availableConfigs.length) return null;

  return (
    <Card style={{ padding: "var(--space-4)", alignSelf: "stretch", flexShrink: 0 }}>
      <Flex direction="column" gap={expanded ? "4" : "0"}>
        <Flex align="center" justify="between" gap="4" wrap="wrap">
          <Flex direction="column" gap="1">
            <Flex align="center" gap="2">
              <Text size="3" weight="medium">Stripe email rate card</Text>
              <Badge color="green" variant="soft">Live pricing</Badge>
            </Flex>
            <Text size="1" color="gray">Loaded from the metered Price configured in Stripe.</Text>
          </Flex>
          <Button
            type="button"
            size="2"
            variant="soft"
            color="gray"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            {expanded ? "Hide rates" : "View rates"}
          </Button>
        </Flex>

        {expanded && availableConfigs.map((config, configIndex) => {
          const rows = getPriceRateRows(config);
          const currency = CURRENCY_SYMBOLS[config.currency?.toLowerCase()] || fallbackCurrency;
          const hasFlatAmounts = rows.some((row) => row.flatAmount !== 0);
          const isGraduated = config.billing_scheme === "tiered" && config.tiers_mode === "graduated";
          const isVolume = config.billing_scheme === "tiered" && config.tiers_mode === "volume";

          return (
            <Flex key={config.price_id || configIndex} direction="column" gap="2">
              <Flex align="center" gap="2" wrap="wrap">
                <Badge color="gray" variant="soft">
                  {isGraduated ? "Graduated pricing" : isVolume ? "Volume pricing" : "Per-email pricing"}
                </Badge>
                <Text size="1" color="gray">
                  {isGraduated
                    ? "Each rate applies only to emails within that range."
                    : isVolume
                    ? "The reached tier's rate applies to all emails in the billing period."
                    : "The same rate applies to every email."}
                </Text>
              </Flex>
              <Box style={{ maxHeight: 360, overflow: "auto", borderRadius: "var(--radius-2)" }}>
                <Table.Root variant="surface" size="2" style={{ width: "100%" }}>
                  <Table.Header style={{ position: "sticky", top: 0, background: "var(--color-panel-solid)", zIndex: 1 }}>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Email volume</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell justify="end">Charge per email</Table.ColumnHeaderCell>
                      {hasFlatAmounts && <Table.ColumnHeaderCell justify="end">Additional tier fee</Table.ColumnHeaderCell>}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rows.map((row) => (
                      <Table.Row key={row.key}>
                        <Table.Cell>{row.range}</Table.Cell>
                        <Table.Cell justify="end" weight="medium">
                          {row.unitAmount === 0 ? "Free" : `${currency}${fmtUnitPrice(row.unitAmount)}`}
                        </Table.Cell>
                        {hasFlatAmounts && (
                          <Table.Cell justify="end">
                            {row.flatAmount === 0 ? "—" : `${currency}${fmt(row.flatAmount)}`}
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
              <Text size="1" color="gray">Prices exclude any applicable taxes.</Text>
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
}

function QuickSelectLimitButtons({ options, value, onChange }) {
  return (
    <Flex gap="2" wrap="wrap">
      {options.map((opt) => (
        <Button
          key={opt}
          size="1"
          variant={value === opt ? "outline" : "soft"}
          color={value === opt ? "iris" : "gray"}
          onClick={() => onChange(opt)}
          style={{ minWidth: "fit-content" }}
        >
          {fmtInt(opt)}
        </Button>
      ))}
    </Flex>
  );
}

function ChannelSpendCard({ icon: Icon, label, cost, billable, pending, awaitingDelivery, delivered, bounced, dropped, currency = CURRENCY }) {
  return (
    <Card style={{ display: "flex", padding: "var(--space-4)", flexDirection: "column", alignItems: "flex-start",
        gap: "var(--space-2)", flex: "1 0 0", alignSelf: "stretch" }}>
      <Flex align="center" gap="2">
        <Icon style={{ color: "gray", width: 16, height: 16 }} />
        <Text size="2" color="gray" weight="medium">{label}</Text>
      </Flex>
      <Text size="6" weight="bold">
        {currency}
        {fmt(cost)}
      </Text>
      <Text size="2" color="gray" weight="medium">
        {fmtInt(billable)} accepted submissions this billing period
      </Text>
      <Flex gap="3" wrap="wrap" align="center">
        <Badge size="1" color="blue" variant="outline">{fmtInt(pending)} in-flight reservations</Badge>
        <Badge size="1" color="amber" variant="outline">{fmtInt(awaitingDelivery)} awaiting outcome</Badge>
        <Badge size="1" color="green" variant="outline">{fmtInt(delivered)} Delivered</Badge>
        <Badge size="1" color="orange" variant="outline">{fmtInt(bounced)} Bounced</Badge>
        <Badge size="1" color="red" variant="outline">{fmtInt(dropped)} Dropped</Badge>
      </Flex>
    </Card>
  );
}

function ChannelLimitRow({ channel, expanded, onToggleExpand, onSaveLimit, isSaving, currency = CURRENCY }) {
  const [limitOn, setLimitOn] = useState(channel.limitStatus);
  const [limitValue, setLimitValue] = useState(channel.limitAmount);
  const [inputValue, setInputValue] = useState(String(channel.limitAmount));

  useEffect(() => {
    setLimitOn(channel.limitStatus);
    setLimitValue(channel.limitAmount);
    setInputValue(String(channel.limitAmount));
  }, [channel.limitAmount, channel.limitStatus]);

  useEffect(() => {
    if (!limitOn && expanded) {
      onToggleExpand?.();
    }
  }, [expanded, limitOn, onToggleExpand]);

  const draftLimitValue = Number(inputValue) || limitValue;
  const displayLimitValue = expanded ? draftLimitValue : limitValue;
  const estimatedCharge = estimateChargeForQuantityConfigs(channel.priceEstimationConfigs, displayLimitValue);
  const displayEstimatedCharge = estimatedCharge ?? channel.limitEstimatedCharge;
  const capacityUsed = channel.billable + channel.pending;
  const pct = displayLimitValue > 0 ? Math.min((capacityUsed / displayLimitValue) * 100, 100) : 100;
  const remaining = Math.max(displayLimitValue - capacityUsed, 0);
  const nearLimit = pct >= 80;
  const Icon = channel.icon;

  const saveLimit = (nextLimitOn = limitOn, nextLimitValue = limitValue) => {
    onSaveLimit?.(nextLimitOn ? nextLimitValue : null);
  };

  return (
    <Card style={{ display: "flex", flexDirection: "column", padding: "var(--space-4)",
        gap: "var(--space-4)", alignSelf: "stretch", flexShrink: 0 }}>
      <Flex align="center" justify="between" style={{ alignSelf: "stretch" }}>
        <Flex align="center" gap="3">
          <Flex align="center" justify="center"
            style={{ width: 32, height: 32, borderRadius: "var(--radius-2)", background: "var(--gray-a3)" }}>
            <Icon style={{ color: "gray", width: 16, height: 16 }} />
          </Flex>
          <Text size="3" weight="medium">
            {channel.label}
          </Text>
          {nearLimit && limitOn && (
            <Badge color="orange" size="1" variant="soft">
              <ExclamationTriangleIcon style={{ width: 12, height: 12 }} />
              Near limit
            </Badge>
          )}
        </Flex>
        <Flex align="center" gap="4">
          <Text size="2" color="gray">
            {fmtInt(channel.billable)} accepted · {fmtInt(channel.pending)} in-flight
          </Text>
          <Flex align="center" gap="2">
            <Switch
              size="1"
              variant="surface"
              checked={limitOn}
              disabled={isSaving}
              onCheckedChange={(checked) => {
                setLimitOn(checked);
                saveLimit(checked, limitValue);
              }}
            />
            <Text size="1" color="gray">
              {limitOn ? "On" : "Off"}
            </Text>
          </Flex>
          <Flex
            align="center"
            justify="center"
            onClick={limitOn ? onToggleExpand : undefined}
            style={{
              cursor: limitOn ? "pointer" : "not-allowed",
              opacity: limitOn ? 1 : 0.45,
            }}
          >
            {expanded ? (
              <Tooltip content={limitOn ? "Collapse limit settings" : "Enable the limit first"} side="top" sideOffset={10}>
                <ChevronUpIcon width={16} height={16} />
              </Tooltip>
            ) : (
              <Tooltip content={limitOn ? "Expand limit settings" : "Enable the limit first"} side="top" sideOffset={10}>
                <ChevronDownIcon width={16} height={16} />
              </Tooltip>
            )}
          </Flex>
        </Flex>
      </Flex>

      {limitOn && (
        <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
          <ProgressBar value={capacityUsed} max={displayLimitValue} height={6} />
          <Flex justify="between" style={{ alignSelf: "stretch" }} align="center">
            <Text size="1" color="gray">
              {fmtInt(remaining)} remaining · {Math.round(pct)}% of {fmtInt(displayLimitValue)} limit
            </Text>
            {displayEstimatedCharge != null && (
              <Flex align="baseline" gap="1">
                <Text size="1" color="gray">Estimated charge at this submission limit:</Text>
                <Text size="3" weight="bold">
                  {currency}
                  {fmt(displayEstimatedCharge)}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      )}

      {expanded && limitOn && (
        <Flex direction="column" gap="3" style={{ alignSelf: "stretch" }}>
          <Separator size="4" orientation="horizontal" />
          <Text size="2" color="gray">
            Set the {channel.label.toLowerCase()} submission cap for each Stripe billing period. Campaigns pause once reached and must be resumed explicitly.
          </Text>
          <QuickSelectLimitButtons
            options={channel.quickLimits}
            value={limitValue}
            onChange={(v) => {
              setLimitValue(v);
              setInputValue(String(v));
            }}
          />
          <Flex gap="3" align="center">
            <input
              type="number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              style={{
                width: 160,
                padding: "6px 12px",
                borderRadius: "var(--radius-2)",
                border: "1px solid var(--gray-a6)",
                background: "var(--color-panel-solid)",
                color: "var(--gray-12)",
                fontSize: 14,
              }}
            />
            <Button
              size="2"
              disabled={isSaving}
              onClick={() => {
                const nextLimitValue = Number(inputValue) || limitValue;
                setLimitValue(nextLimitValue);
                saveLimit(true, nextLimitValue);
              }}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </Flex>
        </Flex>
      )}
    </Card>
  );
}

function extractErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data.error === "string") return data.error;
  if (typeof data.detail === "string") return data.detail;
  const firstValue = Object.values(data)[0];
  if (typeof firstValue === "string") return firstValue;
  if (Array.isArray(firstValue) && firstValue[0]) return firstValue[0];
  return fallback;
}

// Most DNS providers append the zone to the host, so a fully-qualified host would be doubled up.
function toRelativeHost(host, domain) {
  if (!host || !domain) return host || "";
  const lowerHost = host.toLowerCase();
  const lowerDomain = domain.toLowerCase();
  if (lowerHost === lowerDomain) return "@";
  if (lowerHost.endsWith(`.${lowerDomain}`)) return host.slice(0, host.length - lowerDomain.length - 1);
  return host;
}

const DNS_COLUMNS = [
  { key: "record", label: "Record", width: "12%" },
  { key: "type", label: "Type", width: "10%" },
  { key: "host", label: "Host", width: "26%" },
  { key: "value", label: "Value", width: "30%" },
  { key: "status", label: "Status", width: "22%" },
];

function getDnsRows(records, domain, validationResults) {
  if (!records || typeof records !== "object") return [];

  const seen = new Set();
  return Object.entries(records)
    .map(([key, record]) => {
      const host = record?.host || record?.name || "";
      const result = validationResults?.[key];
      return {
        key,
        type: (record?.type || "CNAME").toUpperCase(),
        host,
        relativeHost: toRelativeHost(host, domain),
        value: record?.data || record?.value || "",
        valid: record?.valid,
        reason: typeof result?.reason === "string" ? result.reason : null,
      };
    })
    .filter((row) => {
      // Older records stored a duplicate of mail_cname under the key "url".
      const identity = `${row.host}|${row.value}`;
      if (seen.has(identity)) return false;
      seen.add(identity);
      return true;
    });
}

function DnsRecordsTable({ title, hint, records, domain, validationResults, optional, onCopy }) {
  const rows = getDnsRows(records, domain, validationResults);

  if (!rows.length) {
    return <Text size="2" color="gray">{title} DNS records will appear after SendGrid creates them.</Text>;
  }

  return (
    <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
      <Flex align="center" gap="2">
        <Text size="3" weight="medium">{title}</Text>
        {hint && (
          <Tooltip content={hint} side="right" sideOffset={6}>
            <InfoCircledIcon />
          </Tooltip>
        )}
      </Flex>
      <Table.Root variant="surface" size="1" style={{ width: "100%", tableLayout: "fixed" }}>
        <Table.Header>
          <Table.Row>
            {DNS_COLUMNS.map((column) => (
              <Table.ColumnHeaderCell key={column.key} style={{ width: column.width }}>
                {column.label}
              </Table.ColumnHeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row) => (
            <Table.Row key={row.key}>
              <Table.Cell><Text size="1" weight="medium" style={{ overflowWrap: "anywhere" }}>{row.key}</Text></Table.Cell>
              <Table.Cell><Badge color="gray" variant="soft">{row.type}</Badge></Table.Cell>
              <Table.Cell>
                <Flex direction="column" gap="1">
                  <Flex align="center" gap="3">
                    <Text size="1" weight="medium" style={{ overflowWrap: "anywhere" }}>{row.relativeHost}</Text>
                    <Tooltip content="Copy host (use this on Squarespace, GoDaddy, Cloudflare)" side="top" sideOffset={6}>
                      <Button size="1" variant="ghost" color="gray" onClick={() => onCopy(row.relativeHost)}>
                        <CopyIcon />
                      </Button>
                    </Tooltip>
                  </Flex>
                  {row.relativeHost !== row.host && (
                    <Flex align="center" gap="3">
                      <Text size="1" color="gray" style={{ overflowWrap: "anywhere" }}>{row.host}</Text>
                      <Tooltip content="Copy full name (only if your provider does not append your domain)" side="top" sideOffset={6}>
                        <Button size="1" variant="ghost" color="gray" onClick={() => onCopy(row.host)}>
                          <CopyIcon />
                        </Button>
                      </Tooltip>
                    </Flex>
                  )}
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Flex align="center" gap="3">
                  <Text size="1" style={{ overflowWrap: "anywhere" }}>{row.value}</Text>
                  <Button size="1" variant="ghost" color="gray" onClick={() => onCopy(row.value)}>
                    <CopyIcon />
                  </Button>
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Flex direction="column" gap="1" align="start">
                  {optional ? (
                    <Badge color="gray" variant="soft">Recommended</Badge>
                  ) : row.valid === true ? (
                    <Badge color="green" variant="soft">Valid</Badge>
                  ) : row.valid === false ? (
                    <Badge color="amber" variant="soft">Waiting</Badge>
                  ) : (
                    <Badge color="gray" variant="soft">Unknown</Badge>
                  )}
                  {!optional && row.valid !== true && row.reason && (
                    <Text size="1" color="gray" style={{ overflowWrap: "anywhere" }}>{row.reason}</Text>
                  )}
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Flex>
  );
}

function AddDomainDialog({ open, onOpenChange, organisationId, onCreate, isCreating, errorMessage, onClearError }) {
  const { locationsData } = useLocationContext();
  const { data: brandsData } = useGetAllBrands(organisationId);
  const [form, setForm] = useState({
    scope: "organisation",
    brand: "",
    locations: [],
    domain: "",
    default_sender_email: "",
    default_sender_name: "",
  });

  const brandOptions = useMemo(
    () => (brandsData || []).filter((brand) => !brand.is_default),
    [brandsData]
  );

  const independentLocationOptions = useMemo(
    () => (locationsData || [])
      .filter((location) => {
        const currentBrand = (brandsData || []).find((b) => b.id === location.brand_id);
        return !currentBrand || currentBrand.is_default;
      })
      .map((location) => ({ value: location.id, label: location.name })),
    [brandsData, locationsData]
  );

  const hasScopeTarget =
    form.scope === "organisation" ||
    (form.scope === "brand" && form.brand) ||
    (form.scope === "location" && form.locations.length > 0);
  const canSubmit = form.domain && form.default_sender_email && form.default_sender_name && organisationId && hasScopeTarget;

  const updateField = (field, value) => {
    onClearError?.();
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateScope = (scope) => {
    onClearError?.();
    setForm((current) => ({ ...current, scope, brand: "", locations: [] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    const created = await onCreate({ organisation: organisationId, ...form });
    if (created) {
      setForm({ scope: "organisation", brand: "", locations: [], domain: "", default_sender_email: "", default_sender_name: "" });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: 520 }}>
        <Dialog.Title>Add Sender Domain</Dialog.Title>
        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <Box>
              <Text as="label" size="2" weight="medium">Applies to</Text>
              <Select.Root value={form.scope} onValueChange={updateScope}>
                <Select.Trigger style={{ width: "100%", marginTop: 6 }} />
                <Select.Content>
                  <Select.Item value="organisation">Whole organisation</Select.Item>
                  <Select.Item value="brand">Brand</Select.Item>
                  <Select.Item value="location">Independent locations</Select.Item>
                </Select.Content>
              </Select.Root>
            </Box>

            {form.scope === "brand" && (
              <Box>
                <Text as="label" size="2" weight="medium">Brand</Text>
                <Select.Root value={form.brand} onValueChange={(value) => updateField("brand", value)}>
                  <Select.Trigger placeholder="Select brand" style={{ width: "100%", marginTop: 6 }} />
                  <Select.Content>
                    {brandOptions.map((brand) => (
                      <Select.Item key={brand.id} value={String(brand.id)}>{brand.name}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Box>
            )}

            {form.scope === "location" && (
              <Box>
                <Text as="label" size="2" weight="medium">Independent locations</Text>
                <Box style={{ marginTop: 6 }}>
                  <LocationMultiSelect
                    width="100%"
                    options={independentLocationOptions}
                    defaultValue={form.locations}
                    onValueChange={(value) => updateField("locations", value)}
                    displayTextType="generic"
                  />
                </Box>
              </Box>
            )}

            <Box>
              <Text as="label" size="2" weight="medium" htmlFor="domain">Domain</Text>
              <TextField.Root
                id="domain"
                placeholder="clientdomain.com"
                value={form.domain}
                onChange={(event) => updateField("domain", event.target.value)}
                style={{ marginTop: 6 }}
              />
            </Box>
            <Box>
              <Text as="label" size="2" weight="medium" htmlFor="sender-email">Default sender email</Text>
              <TextField.Root
                id="sender-email"
                placeholder="marketing@clientdomain.com"
                value={form.default_sender_email}
                onChange={(event) => updateField("default_sender_email", event.target.value)}
                style={{ marginTop: 6 }}
              />
            </Box>
            <Box>
              <Text as="label" size="2" weight="medium" htmlFor="sender-name">Default sender name</Text>
              <TextField.Root
                id="sender-name"
                placeholder="Client Brand"
                value={form.default_sender_name}
                onChange={(event) => updateField("default_sender_name", event.target.value)}
                style={{ marginTop: 6 }}
              />
            </Box>
            {errorMessage && (
              <Text size="2" color="red">
                {errorMessage}
              </Text>
            )}
            <Flex justify="end" gap="2">
              <Dialog.Close>
                <Button type="button" variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button type="submit" disabled={!canSubmit || isCreating}>
                {isCreating ? "Adding..." : "Add domain"}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function DomainCard({ domain, onCopy, onVerify, onUpdate, onDelete, isVerifying, isUpdating, isDeleting }) {
  const [expanded, setExpanded] = useState(false);
  const [senderName, setSenderName] = useState(domain.default_sender_name || "");
  const [senderEmail, setSenderEmail] = useState(domain.default_sender_email || "");
  const statusColor = STATUS_COLOR[domain.status] || "gray";
  const linkStatusColor = STATUS_COLOR[domain.link_status] || "gray";
  const isDomainVerified = domain.status === "verified";
  const isLinkVerified = domain.link_status === "verified";
  const isFullyVerified = isDomainVerified && isLinkVerified;
  const hasChanged = senderName !== domain.default_sender_name || senderEmail !== domain.default_sender_email;
  const locationCount = domain.location_details?.length || 0;
  const scopeLabel = domain.scope === "brand"
    ? `Brand: ${domain.brand_name || "Unknown"}`
    : domain.scope === "location"
    ? `${locationCount} independent location${locationCount === 1 ? "" : "s"}: ${domain.location_details?.map((loc) => loc.name).join(", ") || "Unknown"}`
    : "Whole organisation";

  return (
    <Card style={{ alignSelf: "stretch", padding: "var(--space-4)" }}>
      <Flex direction="column" gap="4">
        {/* Collapsed header - always visible */}
        <Flex align="center" justify="between" style={{ alignSelf: "stretch" }}>
          <Flex align="center" gap="3">
            <Flex direction="column" gap="1">
              <Flex align="center" gap="2" wrap="wrap">
                <Text size="3" weight="medium">{domain.domain}</Text>
                <Tooltip content="Domain authentication (em / DKIM records)" side="top" sideOffset={6}>
                  <Badge color={statusColor} variant="soft">Auth: {domain.status}</Badge>
                </Tooltip>
                <Tooltip content="Link branding (links records)" side="top" sideOffset={6}>
                  <Badge color={linkStatusColor} variant="soft">Links: {domain.link_status || "pending"}</Badge>
                </Tooltip>
                {domain.is_active && <Badge color="blue" variant="soft">Active</Badge>}
              </Flex>
              <Text size="1" color="gray">{domain.default_sender_name} &lt;{domain.default_sender_email}&gt;</Text>
            </Flex>
          </Flex>
          <Flex align="center" gap="4">
            <Badge color="gray" variant="soft">{scopeLabel}</Badge>
            {domain.last_error && (
              <Tooltip content={domain.last_error}>
                <ExclamationTriangleIcon
                  style={{ color: isFullyVerified ? "var(--red-9)" : "var(--amber-9)", width: 16, height: 16 }}
                />
              </Tooltip>
            )}
            <Flex
              align="center"
              justify="center"
              onClick={() => setExpanded(!expanded)}
              style={{ cursor: "pointer" }}
            >
              {expanded ? (
                <Tooltip content="Collapse" side="top" sideOffset={10}>
                  <ChevronUpIcon width={16} height={16} />
                </Tooltip>
              ) : (
                <Tooltip content="Expand" side="top" sideOffset={10}>
                  <ChevronDownIcon width={16} height={16} />
                </Tooltip>
              )}
            </Flex>
          </Flex>
        </Flex>

        {/* Expanded details */}
        {expanded && (
          <Flex direction="column" gap="3" style={{ alignSelf: "stretch" }}>
            <Separator size="4" orientation="horizontal" />
            {/* Action buttons */}
            <Flex gap="2" wrap="wrap" justify="end">
              {!isFullyVerified && (
                <Button size="2" variant="solid" color="teal"
                  onClick={() => onVerify(domain.sender_domain_id)} disabled={isVerifying}>
                  <ReloadIcon />
                  Verify domain
                </Button>
              )}
              {isFullyVerified && !domain.is_active && (
                <Button size="2" variant="soft" onClick={() => onUpdate(domain.sender_domain_id, { is_active: true })} disabled={isUpdating}>
                  <CheckCircledIcon />
                  Make active
                </Button>
              )}
              <Button size="2" variant="solid" color="red" 
                onClick={() => onDelete(domain.sender_domain_id)} disabled={isDeleting}>
                <TrashIcon />
                Delete
              </Button>
            </Flex>

            {/* Sender edit fields */}
            <Flex gap="3" wrap="wrap" align="end">
              <Box style={{ flex: "1 1 220px" }}>
                <Text as="label" size="2" weight="medium">Sender email</Text>
                <TextField.Root value={senderEmail} onChange={(event) => setSenderEmail(event.target.value)} style={{ marginTop: 6 }} />
              </Box>
              <Box style={{ flex: "1 1 220px" }}>
                <Text as="label" size="2" weight="medium">Sender name</Text>
                <TextField.Root value={senderName} onChange={(event) => setSenderName(event.target.value)} style={{ marginTop: 6 }} />
              </Box>
              <Button
                variant="soft"
                disabled={!hasChanged || isUpdating}
                onClick={() => onUpdate(domain.sender_domain_id, { default_sender_email: senderEmail, default_sender_name: senderName })}
              >
                Save sender
              </Button>
            </Flex>

            {domain.last_error && <Text size="2" color="gray">{domain.last_error}</Text>}
            <Callout.Root size="1" color="amber">
              <Callout.Icon>
                <InfoCircledIcon />
              </Callout.Icon>
              <Callout.Text>
                The domain authentication and link branding records below are generated by SendGrid. Add them to your 
                DNS provider to verify the domain and enable link branding. It may take up to 24 hours for DNS changes to 
                propagate. Some DNS providers may require you to enter the full host name (e.g., "s1._domainkey.clientdomain.com") 
                instead of the relative host (e.g., "s1._domainkey").
              </Callout.Text>
            </Callout.Root>
            <DnsRecordsTable title="Domain authentication" records={domain.dns_records} domain={domain.domain}
              hint="Proves you own this domain and lets us sign every email with your own DKIM keys. Mailboxes then
                show the message as sent from your domain instead of 'via' ours, which is what protects your
                deliverability. Required, the domain cannot send until these records are valid."
              validationResults={domain.validation_results} onCopy={onCopy} />
            <DnsRecordsTable title="Link branding" records={domain.link_dns_records} domain={domain.domain}
              hint="Rewrites the tracked links and images inside your emails to use your own domain instead of
                sendgrid.net. Without it recipients see an unfamiliar URL when they hover a link, which triggers spam
                filters and hurts click rates. Required, the domain cannot be made active until these records are valid."
              validationResults={domain.link_validation_results} onCopy={onCopy} />
            <DnsRecordsTable title="DMARC (recommended)" records={domain.recommended_dns_records} domain={domain.domain}
              hint="We do not check this record, so it always shows Recommended whether or not the domain already has
                DMARC set up, and verification passes either way. Gmail and Yahoo require DMARC from bulk senders, so
                check your DNS provider for an existing _dmarc record: keep it if there is one, otherwise add the
                record shown here."
              optional onCopy={onCopy} />
          </Flex>
        )}
      </Flex>
    </Card>
  );
}

export default function MarketingUsageSettings() {
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const organisationId = userProfile?.organisation_id;
  const [activeSegment, setActiveSegment] = useState("usage");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addDomainError, setAddDomainError] = useState("");

  // Channel expand state
  const [expandedChannel, setExpandedChannel] = useState(null);

  const { senderDomains, isLoading } = useMarketingSenderDomains(organisationId);
  const createDomainMutation = useCreateMarketingSenderDomain();
  const verifyDomainMutation = useVerifyMarketingSenderDomain();
  const updateDomainMutation = useUpdateMarketingSenderDomain();
  const deleteDomainMutation = useDeleteMarketingSenderDomain();
  const {
    usage: emailMarketingUsage,
    isLoading: isUsageLoading,
    error: emailUsageError,
  } = useEmailMarketingUsage(organisationId);
  const updateUsageLimitMutation = useUpdateEmailMarketingUsageLimit(organisationId);

  const activeDomains = useMemo(() => senderDomains.filter((domain) => domain.is_active), [senderDomains]);
  const emailUsageChannel = emailMarketingUsage?.channels?.find((channel) => channel.key === "email");
  const usageCurrency = CURRENCY_SYMBOLS[emailMarketingUsage?.currency?.toLowerCase()] || CURRENCY;
  const usageChannels = useMemo(
    () => emailUsageError ? [] : MOCK_CHANNELS.map((channel) => {
      if (channel.key !== "email" || !emailUsageChannel) {
        return channel;
      }

      return {
        ...channel,
        sent: Number(emailUsageChannel.sent ?? channel.sent),
        billable: Number(emailUsageChannel.billable ?? emailUsageChannel.sent ?? channel.billable),
        pending: Number(emailUsageChannel.pending ?? channel.pending),
        awaitingDelivery: Number(emailUsageChannel.awaiting_delivery ?? channel.awaitingDelivery),
        delivered: Number(emailUsageChannel.delivered ?? channel.delivered),
        bounced: Number(emailUsageChannel.bounced ?? channel.bounced),
        dropped: Number(emailUsageChannel.dropped ?? channel.dropped),
        remaining: emailUsageChannel.usage_remaining,
        cost: Number(emailUsageChannel.spent_so_far ?? channel.cost),
        limitAmount: emailUsageChannel.usage_limit ?? channel.limitAmount,
        limitStatus: emailUsageChannel.usage_limit != null,
        limitEstimatedCharge: emailUsageChannel.usage_limit_estimated_charge,
        priceEstimationConfigs: emailUsageChannel.usage_price_estimation_configs,
      };
    }),
    [emailUsageChannel, emailUsageError]
  );

  // Derived values
  const totalSpent = usageChannels.reduce((sum, c) => sum + c.cost, 0);
  const projectedSpend = Number(emailUsageChannel?.projected_by_period_end ?? 0);
  const projectedQuantity = Number(emailUsageChannel?.projected_quantity ?? 0);
  const periodStart = emailMarketingUsage?.period?.start
    ? fmtDate(emailMarketingUsage.period.start)
    : null;
  const periodEnd = emailMarketingUsage?.period?.end
    ? fmtDate(emailMarketingUsage.period.end)
    : null;
  const trackingStartedAt = emailMarketingUsage?.period?.tracking_started_at
    ? fmtDateTime(emailMarketingUsage.period.tracking_started_at)
    : null;

  const copyValue = async (value) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setToastMessage("Copied");
  };

  const createDomain = async (payload) => {
    setAddDomainError("");
    try {
      await createDomainMutation.mutateAsync(payload);
      setIsAddDialogOpen(false);
      setToastMessage("Domain added");
      return true;
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to add domain");
      setAddDomainError(message);
      setToastMessage(message);
      return false;
    }
  };

  const verifyDomain = async (senderDomainId) => {
    try {
      const result = await verifyDomainMutation.mutateAsync(senderDomainId);
      const isFullyVerified = result.status === "verified" && result.link_status === "verified";
      setToastMessage(isFullyVerified ? "Domain verified" : result.last_error || "DNS records are still pending");
    } catch (error) {
      setToastMessage(extractErrorMessage(error, "Failed to verify domain"));
    }
  };

  const updateDomain = async (senderDomainId, data) => {
    try {
      await updateDomainMutation.mutateAsync({ senderDomainId, data });
      setToastMessage(data.is_active ? "Active sender updated" : "Sender updated");
    } catch (error) {
      setToastMessage(extractErrorMessage(error, "Failed to update domain"));
    }
  };

  const deleteDomain = async (senderDomainId) => {
    try {
      await deleteDomainMutation.mutateAsync(senderDomainId);
      setToastMessage("Domain deleted");
    } catch (error) {
      setToastMessage(extractErrorMessage(error, "Failed to delete domain"));
    }
  };

  const saveEmailUsageLimit = async (usageLimit) => {
    try {
      await updateUsageLimitMutation.mutateAsync(usageLimit);
      setToastMessage(usageLimit == null ? "Email usage limit disabled" : "Email usage limit saved");
    } catch (error) {
      setToastMessage(extractErrorMessage(error, "Failed to save email usage limit"));
    }
  };

  return (
    <>
    <Flex direction="column" gap="5" style={{ alignSelf: "stretch", width: "100%", height: "100%" }}>
      <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
        <SegmentedControl.Root value={activeSegment} onValueChange={setActiveSegment}>
          <SegmentedControl.Item value="usage">Usage</SegmentedControl.Item>
          <SegmentedControl.Item value="domain">Domains</SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      {activeSegment === "usage" && (
      <Flex direction="column" gap="4" pr="3" style={{ alignSelf: "stretch", height: "100%", overflowY: "auto" }}>

      {emailUsageError && (
        <Card style={{ padding: "var(--space-4)", borderColor: "var(--red-a7)" }}>
          <Flex direction="column" gap="1">
            <Text size="3" weight="medium" color="red">Email usage billing needs configuration</Text>
            <Text size="2" color="gray">
              {extractErrorMessage(emailUsageError, "The metered email product and primary payer could not be resolved.")}
            </Text>
          </Flex>
        </Card>
      )}

      {isUsageLoading && !emailMarketingUsage && (
        <Card style={{ padding: "var(--space-4)" }}><Text color="gray">Loading usage…</Text></Card>
      )}

      <Flex gap="3" style={{ alignSelf: "stretch", flexShrink: 0 }}>
        {usageChannels.map((ch) => (
          <ChannelSpendCard
            key={ch.key}
            icon={ch.icon}
            iconColor={ch.iconColor}
            label={ch.label}
            cost={ch.cost}
            billable={ch.billable}
            pending={ch.pending}
            awaitingDelivery={ch.awaitingDelivery}
            delivered={ch.delivered}
            bounced={ch.bounced}
            dropped={ch.dropped}
            currency={usageCurrency}
          />
        ))}
      </Flex>

      {/* Summary rows */}
      <Card style={{ display: "flex", flexDirection: "column", padding: "var(--space-4)", gap: "var(--space-2)",
        alignSelf: "stretch", flexShrink: 0 }}>
        <Flex justify="between" style={{ alignSelf: "stretch" }}>
          <Text size="2" color="gray">Spent so far</Text>
          <Text size="2" weight="medium">
            {usageCurrency}
            {fmt(totalSpent)}
          </Text>
        </Flex>
        <Flex justify="between" style={{ alignSelf: "stretch" }}>
          <Text size="2" color="gray">Projected by end of billing period</Text>
          <Text size="2" weight="medium">
            {usageCurrency}
            {fmt(projectedSpend)}
          </Text>
        </Flex>
        <Flex justify="between" style={{ alignSelf: "stretch" }}>
          <Text size="2" color="gray">Projected accepted submissions</Text>
          <Text size="2" weight="medium">{fmtInt(projectedQuantity)}</Text>
        </Flex>
        {emailUsageChannel?.budget_amount != null && (
          <Flex justify="between" style={{ alignSelf: "stretch" }}>
            <Text size="2" color={emailUsageChannel.budget_reached ? "red" : "gray"}>Configured budget alert</Text>
            <Text size="2" weight="medium" color={emailUsageChannel.budget_reached ? "red" : undefined}>
              {usageCurrency}{fmt(emailUsageChannel.budget_amount)}
            </Text>
          </Flex>
        )}
        {periodStart && periodEnd && (
          <Flex justify="between" style={{ alignSelf: "stretch" }}>
            <Text size="2" color="gray">Stripe billing period</Text>
            <Text size="2" weight="medium">{periodStart} to {periodEnd}</Text>
          </Flex>
        )}
        {trackingStartedAt && (
          <Flex justify="between" style={{ alignSelf: "stretch" }}>
            <Text size="2" color="gray">Usage tracking started</Text>
            <Text size="2" weight="medium">{trackingStartedAt}</Text>
          </Flex>
        )}
      </Card>

      <StripeRateCard
        configs={emailUsageChannel?.usage_price_estimation_configs}
        fallbackCurrency={usageCurrency}
      />

      {/* Email Usage Limit */}
      <Flex direction="column" gap="1" style={{ alignSelf: "stretch", flexShrink: 0 }}>
        <Text size="4" weight="bold">Email usage limit</Text>
        <Text size="1" color="gray">
          Every non-test email accepted by SendGrid is reported to Stripe, including emails that later 
          bounce or drop. Free and paid ranges follow the Stripe rate card shown above. Pending 
          reservations also consume capacity so concurrent sends cannot exceed the hard cap.
        </Text>
      </Flex>

      {usageChannels.map((ch) => (
        <ChannelLimitRow
          key={ch.key}
          channel={ch}
          expanded={expandedChannel === ch.key}
          onToggleExpand={() =>
            setExpandedChannel(expandedChannel === ch.key ? null : ch.key)
          }
          onSaveLimit={saveEmailUsageLimit}
          isSaving={updateUsageLimitMutation.isPending}
          currency={usageCurrency}
        />
      ))}
      </Flex>
      )}

      {activeSegment === "domain" && (
        <Flex direction="column" gap="4" pr="3" style={{ alignSelf: "stretch", height: "100%", overflowY: "auto" }}>
          <Flex justify="between" align="center" gap="4" wrap="wrap">
            <Flex direction="column" gap="1">
                <Flex gap="2" align="center">
                    <Text size="4" weight="bold">Custom email domain</Text>
                    <Tooltip content="By default, your emails are sent from our system domain. Connect your own domain to improve deliverability and brand recognition." side="right" sideOffset={10}>
                        <InfoCircledIcon />
                    </Tooltip>
                </Flex>
                <Flex gap="2" align="center" wrap="wrap">
                    <Text size="2" color="gray" weight="medium">Active {activeDomains.length === 1 ? "sender" : "senders"}:</Text>
                    {activeDomains.length === 0 ? (
                      <Badge size="2" color="amber" variant="soft">noreply@b-connect.co.uk (default)</Badge>
                    ) : (
                      activeDomains.map((d) => (
                        <Badge key={d.sender_domain_id} size="2" color="green" variant="soft">
                          {d.default_sender_name} &lt;{d.default_sender_email}&gt;
                        </Badge>
                      ))
                    )}
                </Flex>
            </Flex>
            <Button variant="solid" onClick={() => setIsAddDialogOpen(true)}>
              <PlusIcon />
              Add domain
            </Button>
          </Flex>

          {isLoading ? (
            <Card style={{ padding: "var(--space-4)" }}><Text color="gray">Loading domains...</Text></Card>
          ) : senderDomains.length === 0 ? (
            <Flex direction="column" style={{justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch',
                border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)', height: '100%', padding: 'var(--space-6)'}}>
                <Flex direction="column" align="center" gap="2" maxWidth="400px" style={{alignItems: 'center', width: '100%'}}>
                    <Text size="4" weight="medium" align="center">No custom domain connected.</Text>
                    <Text size="2" weight="regular" align="center" color="gray">
                        Add a new domain to start sending emails from your own brand's domain.
                    </Text>
                </Flex>
            </Flex>
          ) : (
            <Flex direction="column" gap="3" style={{ alignSelf: "stretch" }}>
              {senderDomains.map((domain) => (
                <DomainCard
                  key={domain.sender_domain_id}
                  domain={domain}
                  onCopy={copyValue}
                  onVerify={verifyDomain}
                  onUpdate={updateDomain}
                  onDelete={deleteDomain}
                  isVerifying={verifyDomainMutation.isPending}
                  isUpdating={updateDomainMutation.isPending}
                  isDeleting={deleteDomainMutation.isPending}
                />
              ))}
            </Flex>
          )}
        </Flex>
      )}

      <AddDomainDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        organisationId={organisationId}
        onCreate={createDomain}
        isCreating={createDomainMutation.isPending}
        errorMessage={addDomainError}
        onClearError={() => setAddDomainError("")}
      />
    </Flex>
    <ToastAlert />
    </>
  );
}
